import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react'

interface DocumentViewerContextType {
  isOpen: boolean
  documentTitle: string
  documentType: string
  documentUrl: string | null
  documentError: string | null
  openDocument: (title?: string, type?: string, url?: string | null, error?: string | null) => void
  closeDocument: () => void
}

const DocumentViewerContext = createContext<DocumentViewerContextType | undefined>(undefined)

interface DocumentViewerProviderProps {
  children: ReactNode
}

function revokeBlobUrl(url: string | null) {
  if (url?.startsWith('blob:')) URL.revokeObjectURL(url)
}

export const DocumentViewerProvider: React.FC<DocumentViewerProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [documentTitle, setDocumentTitle] = useState('Document Viewer')
  const [documentType, setDocumentType] = useState('PDF')
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [documentError, setDocumentError] = useState<string | null>(null)
  const blobUrlRef = useRef<string | null>(null)

  useEffect(() => {
    blobUrlRef.current = documentUrl
  }, [documentUrl])

  const closeDocument = useCallback(() => {
    revokeBlobUrl(blobUrlRef.current)
    blobUrlRef.current = null
    setDocumentUrl(null)
    setDocumentError(null)
    setIsOpen(false)
  }, [])

  const openDocument = useCallback((
    title: string = 'Document Viewer',
    type: string = 'PDF',
    url?: string | null,
    error?: string | null,
  ) => {
    const nextUrl = url ?? null
    setDocumentTitle(title)
    setDocumentType(type)
    setDocumentError(error ?? null)
    setDocumentUrl((prev) => {
      if (prev && prev !== nextUrl) revokeBlobUrl(prev)
      return nextUrl
    })
    setIsOpen(true)
  }, [])

  return (
    <DocumentViewerContext.Provider
      value={{
        isOpen,
        documentTitle,
        documentType,
        documentUrl,
        documentError,
        openDocument,
        closeDocument
      }}
    >
      {children}
    </DocumentViewerContext.Provider>
  )
}

export const useDocumentViewer = (): DocumentViewerContextType => {
  const context = useContext(DocumentViewerContext)
  if (context === undefined) {
    throw new Error('useDocumentViewer must be used within a DocumentViewerProvider')
  }
  return context
}
